import { Injectable, inject } from '@angular/core';
import { Query } from '@datorama/akita';
import { CmgCcwStore } from './ccw.store';
import { ClosingCostWorksheet, FeeWorksheetLoanPropertyInfo } from '../../closing-cost-worksheet-15/models/ccw.models';
import { DirtyChecker2 } from '../core/dirty-checker-2';
import { LoanDataModel } from '../../cmg-1003/models';
import { Confirm } from '../../closing-cost-worksheet-15/models/ccw.enums';
import { CcwPropertyInformationModel } from '../../closing-cost-worksheet-15/models/ccw-property-information.model';
import { CcwLoanDataModel } from '../../closing-cost-worksheet-15/models/ccw-loan-data-model';
import { dateWithoutTimeUTC } from '../../shared';

@Injectable({ providedIn: 'root' })
export class CmgCcwQuery extends Query<ClosingCostWorksheet> {
  protected store: CmgCcwStore;

  dirtyChecker2: DirtyChecker2 = new DirtyChecker2(this.store, this);

  constructor() {
    const store = inject(CmgCcwStore);

    super(store);

    this.store = store;
  }

  setHead() {
    this.dirtyChecker2.dirtyChecker.setHead();
  }

  // ----- Mappings -----
  mapCcwToUrlaLoan(ccw: ClosingCostWorksheet): LoanDataModel {
    const info: FeeWorksheetLoanPropertyInfo = ccw.feeWorksheetLoanPropertyInfo;
    const loanData: LoanDataModel = {
      afterImprovedValue: info.afterImprovedValue?.toString(),
      // agencyCaseNumber: N/A
      // aioCashInHand: N/A
      // aioCashInHandId: N/A
      // minimumDrawAmount: ? //TODO
      // aioInitialDrawAmount: ? //TODO
      // aioInitialDrawAmountId: ? //TODO
      // aioLoanNotes: N/A
      // aioLoanNotesId: N/A
      // aioMaxRate: ? //TODO
      // aioMaxRateId: N/A
      // aioWelcomeLetterRequestDate: N/A
      // aioWelcomeLetterRequestDateId: N/A
      // amortizationType: info.amortizationTypeDescOV, //TODO: Needed? doesn't exist as number
      amortizationTypeDescription: info.amortizationTypeDescOV,
      // antiSteering: N/A
      // applicationDate: N/A
      appraisalValue: info.appraisedValue,
      apr: info.apr,
      areYouDisabledVeteran: info.veteranHasDisability === Confirm.Yes,
      // armAddFeatures: ? //TODO
      // armAdjustCapFirst: ? //TODO
      // armAdjustCapSubsequent: ? //TODO
      // armCarryover: ? //TODO
      // armCommonRenewal: ? //TODO
      // armConversionOption: ? //TODO
      // armFloor: ? //TODO
      // armHasNegAmFeature: ? //TODO
      // armIndexNameOV: ? //TODO
      // armIndexType: ? //TODO
      // armIntRateAdjustAt: ? //TODO
      // armIntRateFixedFor: ? //TODO
      armMargin: info.armMargin,
      armIndexValue: info.armIndexValue,
      // armIndexValueForWeekEnding: ? //TODO
      // armIndexPublished: ? //TODO
      // armLifeCap: ? //TODO
      // armLookbackDays: ? //TODO
      // armNegAmDesc: ? //TODO
      // armNoAdjCap: ? //TODO
      // armRounding: ? //TODO
      // armQualRateOption: ? //TODO
      baseLoanAmount: info.baseLoan,
      // borrower1PreferredContactNumber: N/A
      // borrower1PreferredContactNumberId: N/A
      // borrower2PreferredContactNumber: N/A
      // borrower2PreferredContactNumberId: N/A
      caseNumberAssignedDate: info.caseNoAssignmentPeriod,
      channel: ccw.channel,
      cltv: info.cltv,
      constructionPurpose: info.constructionPurpose,
      creditScoreMedian: info.fileCreditScore,
      // demandFeature: ? //TODO
      downPaymentAmount: info.downPaymentAmount,
      downPaymentPercentage: info.downPaymentPercentage,
      // doYouOwnMoreThan4Dwelling: N/A
      dpfactor: info.dpfactor,
      // dwellingCoveredDetails: N/A
      // qualRateReboundLoanNumber: N/A
      // qualRateReboundLoanNumberId: N/A
      // loanPropertyFeeWorksheet: N/A
      // fhaCity: N/A
      // fhaState: N/A
      // fhaStreet: N/A
      // fhaZip: N/A
      // fhaCashOutRefiAcquiredDate: ? //TODO
      // fhaOccupancySeasoningDate: ? //TODO
      // dateOwnedAndOccupied: ? //TODO
      // vaSixMonthlyPaymentDate: ? //TODO
      fileDataId: ccw.fileDataId,
      fileName: ccw.fileName,
      // firstRatio: ? //TODO: double check calculated
      firstPaymentDate: info.firstPaymentDateStringFormat,
      fundingFeeType: info.fundingFeeType,
      // gusFirstRunDate: N/A
      // hasPrepaymentPenalty: N/A
      // haveYouSoldRealEstateIn60Months: N/A
      // helocDrawAccessTerm: ? //TODO
      // helocRepaymentTerm: ? //TODO
      // homeOwnedThroughVa: ? //TPDO
      // householdMembers: ? //TODO USDA household members
      inProcessingDate: info.inProcessingDate,
      interestOnlyPeriod: info.interestOnlyPeriod,
      interestRate: info.intRate,
      // intOnlyQualPaymentCalcOption: N/A
      // isBalloon: N/A
      // isDwellingToBeCovered: N/A
      // isInterestOnly: //TODO: double check calculated
      // isItToBeSold: N/A
      isJumbo: info.isJumbo,
      // isNegativeAmortization: ? //TODO
      // isGrantLoan: ? //TODO
      // isHomeFundItLoan: N/A
      // isOther: //TODO double check calculated
      // isPrepaymentPenalty: N/A
      // isRateReboundRefinance: N/A
      // isRateReboundRefinanceId?: N/A
      // isTemporaryBuydown: N/A
      // lenderCaseNumber: N/A
      // lenderFhaId: N/A
      // lenderVaId: N/A
      loanId: ccw.loanId,
      // loanInfoCombinedHousingExpense: //TODO double check calculated
      loanProgramCode: info.loanProgramCode,
      loanProgramName: info.loanProgramName,
      loanPurpose: info.loanPurpose,
      loanTerm: info.term,
      // balloonTerm: N/A
      lockExpirationDate: info.lockExpirationDate,
      lockStartDate: info.lockStartDate,
      ltv: info.ltv,
      // maxDrawAmountNinetyNinePercentOfLoanAmount: ? //TODO
      // maxDrawAmountNinetyNinePercentOfLoanAmountId: ? //TODO
      // maxLoanLimitByCounty: N/A
      miPaidInCash: info.miPaidInCash,
      mipffFinancingCalcOption: info.miPFFFinancingCalcOption,
      // mipffPercent: N/A
      monthlyMCC: info.monthlyMCC,
      monthlyMiAmount: info.monthlyMIAmount,
      monthlyMiFactor: info.monthlyMIFactor,
      mortgageAppliedFor: info.mortgageType,
      organizationId: ccw.organizationId,
      // originalMortgageAmount: N/A
      originationChannel: ccw.channel,
      otherMortgageDescription: info.mortgageTypeOther,
      otherPurposeDescription: info.loanPurposeOther,
      // pi: //TODO double check calculated
      pmiType: info.pmiType,
      // prepaymentPenaltyMax: N/A
      // prepaymentPenaltyTerm: N/A
      // productDescription: N/A
      // productDescriptionOther: N/A
      // qaCallComplete: N/A
      // qaCallCompleteId: N/A
      // qaCallRequest: N/A
      // qaCallRequestId: N/A
      // qualRate: N/A
      // qualRateOV: N/A
      // referralInfo: N/A
      refinancePurposeAu: info.refiPurpAU,
      refinancePurposeFha: info.refiTypeFHA,
      refinancePurposeVa: info.refiTypeVA,
      // relatedLoans: N/A
      salesPrice: info.salesPrice,
      schedClosingDate: info.estClosingDateStringFormat,
      // signingAppointmentDate: N/A
      // signingAppointmentTime: N/A
      // schedFundingDate: //TODO double check calculated
      // secondRatio: //TODO double check calculated
      sectionOfAct: info.sectionOfAct,
      // soldSalesPriceFha: ? //TODO
      // sponsorFhaid: N/A
      // sponsorVaid: N/A
      // statusId?: N/A
      // subOrdinateFinance: ? //TODO
      totalLoanAmount: info.baseLoan,
      totalMOnthlyPayments: info.totalMOnthlyPayments,
      // unusedFundsLeftOnCreditLine: ? //TODO
      // unusedFundsLeftOnCreditLineId: ? //TODO
      upfrontMiAmount: info.upfrontMiAmount,
      upfrontMiFactor: info.upfrontMIFactor,
      upfrontMiType: info.upfrontMiType,
      // userId: N/A
      earnestAmt: info.earnestMoneyDeposit,
      sellerCredit: info.sellerCredit,
      // OpsLenderCredit: N/A
      // vaSplitFee: N/A
      // vaSplitFeeID: N/A
      fhaExpectedEndorsementDate: info.origFHACaseEndorsementDate, //TODO confirm what maps from origFHACaseEndorsementDate
      fhaPreviousEndorsementDate: info.origFHACaseEndorsementDate, //TODO confirm what maps from origFHACaseEndorsementDate
    };
    return loanData;
  }

  mapUrlaLoanToCcw(loan: CcwLoanDataModel, property: CcwPropertyInformationModel): FeeWorksheetLoanPropertyInfo {
    const info: FeeWorksheetLoanPropertyInfo = {
      // Loan Information
      appraisedValue: loan.appraisalValue,
      salesPrice: loan.salesPrice,
      baseLoan: loan.baseLoanAmount,
      downPaymentAmount: loan.downPaymentAmount,
      downPaymentPercentage: loan.downPaymentPercentage,
      dpfactor: loan.dpfactor,
      intRate: loan.interestRate,
      pmiType: loan.pmiType,
      loanWith: loan.totalLoanAmount,
      currentUnpaidPrincipleBalanceamount: loan.currentUnpaidPrincipleBalanceamount,
      secondSubordinateAmount: loan.secondSubordinateAmount,
      thirdSubordinateAmount: loan.thirdSubordinateAmount,
      fileCreditScore: loan.fileCreditScore,
      lockFloating: loan.lockFloating,
      // Property Information
      sizeInAcres: property.sizeInAcres,
      finalOrReinspection1004dRequired: property.finalOrReinspection1004dRequired,
      rushAppraisal: property.rushAppraisal,
      jumboRequiredSecondAppraisal: property.jumboRequiredSecondAppraisal,
      isLoanANewConstruction: property.isLoanANewConstruction,
      appraisalWaiver: property.appraisalWaiver,
      loCommunicationToDisclosuresAmcValue: property.loCommunicationToDisclosuresAmcValue,
      // Important Dates & Fee Information
      specialFloodHazardArea: loan.specialFloodHazardArea,
      loanCommitmentDate: loan.loanCommitmentDate ? dateWithoutTimeUTC(loan.loanCommitmentDate) : null,
      loanEstimatedClosingDate: loan.loanEstimatedClosingDate,
      loanEstimatedFundingDate: loan.loanEstimatedFundingDate,
      loanFirstPaymentDate: loan.loanFirstPaymentDate,
      daysOfPerDiem: loan.daysOfPerDiem,
      estClosingDateStringFormat: loan.estClosingDateStringFormat,
      estFundingDateStringFormat: loan.estFundingDateStringFormat,
      firstPaymentDateStringFormat: loan.firstPaymentDateStringFormat,
      dailyInterest: loan.dailyInterest,
    };
    return info;
  }
}
